import { useState, useEffect, useCallback } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Plane, Plus, Search, Clock, MapPin, Users, UserPlus, UserCheck,
    Loader2, ArrowRight, Luggage, CheckCircle, XCircle, Clipboard,
    Package, ChevronRight, Pencil, LayoutGrid, Printer, TicketCheck, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getLocalToday } from '../utils/dateUtils';
import { SeatMap } from '../components/SeatMapComponent';
import { generateBoardingPass } from '../utils/boardingPass';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
    scheduled: 'bg-slate-500/10 text-slate-400',
    confirmed: 'bg-blue-500/10 text-blue-400',
    preparing: 'bg-cyan-500/10 text-cyan-400',
    boarding: 'bg-amber-500/10 text-amber-400',
    closed: 'bg-orange-500/10 text-orange-400',
    departed: 'bg-emerald-500/10 text-emerald-400',
    en_route: 'bg-green-100 text-green-800 border border-green-300 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/40',
    arrived: 'bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold dark:bg-emerald-500/20 dark:text-emerald-200 dark:border-emerald-400/50',
    delayed: 'bg-yellow-100 text-yellow-800 border border-yellow-300 dark:bg-yellow-500/15 dark:text-yellow-300 dark:border-yellow-500/40',
    cancelled: 'bg-red-100 text-red-800 border border-red-300 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/40',
};

const statusLabels = {
    scheduled: 'Programado', confirmed: 'Confirmado', preparing: 'Preparando',
    boarding: 'Abordando', closed: 'Cerrado', departed: 'Despegado',
    en_route: 'En Vuelo', arrived: 'Llegado', delayed: 'Demorado', cancelled: 'Cancelado',
};

export default function CounterPage() {
    const { token, hasRole, airline } = useAuth();
    const headers = { Authorization: `Bearer ${token}` };
    const [flights, setFlights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateFilter, setDateFilter] = useState(getLocalToday());
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [passengers, setPassengers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [showAddPax, setShowAddPax] = useState(false);
    const [paxLoading, setPaxLoading] = useState(false);
    const [searchPax, setSearchPax] = useState('');
    const [newPax, setNewPax] = useState({
        first_name: '', last_name: '', document_type: 'passport',
        document_number: '', seat: '', pax_type: 'adult',
        special_requirements: '', baggage_count: 1, baggage_weight_kg: 15,
        // Auditoria 6 - Item 10: igualar walk-up con captura de Ventas
        fare_amount: '', payment_method: 'cash', contact_email: '', contact_phone: '', cabin_class: 'economy'
    });
    const [editPax, setEditPax] = useState(null);
    const [editPaxData, setEditPaxData] = useState({});
    const [seatData, setSeatData] = useState(null);
    const [counterTab, setCounterTab] = useState('passengers');
    // Historial de boarding passes emitidos: reimpresion preserva el QR original
    const [boardingPasses, setBoardingPasses] = useState([]);
    const [issuingBp, setIssuingBp] = useState(null);
    // Reservas de Ventas listas para check-in inline (Auditoria 4 - Item 3)
    const [reservations, setReservations] = useState([]);
    // Standby / Espacio Disponible (Auditoria 4 - Stage 4 - Item 18)
    const [standbyData, setStandbyData] = useState(null); // { capacity, used, free_seats, standby: [] }
    const [showStandbyDialog, setShowStandbyDialog] = useState(false);
    const [newStandby, setNewStandby] = useState({ passenger_name: '', contact_phone: '', pax_count: 1, priority: 'normal', notes: '' });
    const [savingStandby, setSavingStandby] = useState(false);

    const fetchFlights = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/counter/flights?date=${dateFilter}`, { headers });
            setFlights(res.data);
        } catch { toast.error('Error al cargar vuelos'); }
        finally { setLoading(false); }
    }, [dateFilter]);

    useEffect(() => { fetchFlights(); }, [fetchFlights]);
    // Auditoria 4 - Item 19: auto-refresh 30s (lista de vuelos)
    useAutoRefresh(fetchFlights, 30000);

    const selectFlight = async (flight) => {
        setSelectedFlight(flight);
        setPaxLoading(true);
        setCounterTab('passengers');
        try {
            const [pRes, sRes] = await Promise.all([
                axios.get(`${API}/counter/flights/${flight.id}/passengers`, { headers }),
                axios.get(`${API}/counter/flights/${flight.id}/summary`, { headers })
            ]);
            setPassengers(pRes.data);
            setSummary(sRes.data);
        } catch { toast.error('Error al cargar pasajeros'); }
        finally { setPaxLoading(false); }
        // Fetch reservations (pending check-in view — Auditoria 4 Item 3)
        try {
            const rvRes = await axios.get(`${API}/reservations?flight_id=${flight.id}`, { headers });
            setReservations(rvRes.data || []);
        } catch { setReservations([]); }
        // Fetch seat map in background
        try {
            const smRes = await axios.get(`${API}/flights/${flight.id}/seats`, { headers });
            setSeatData(smRes.data);
        } catch { setSeatData(null); }
        // Fetch boarding passes history for this flight
        try {
            const bpRes = await axios.get(`${API}/boarding-passes?flight_id=${flight.id}`, { headers });
            setBoardingPasses(bpRes.data || []);
        } catch { setBoardingPasses([]); }
        // Fetch standby list (Item 18)
        try {
            const sbRes = await axios.get(`${API}/reservations/standby/${flight.id}`, { headers });
            setStandbyData(sbRes.data);
        } catch { setStandbyData(null); }
    };

    const refreshStandby = async () => {
        if (!selectedFlight) return;
        try {
            const sbRes = await axios.get(`${API}/reservations/standby/${selectedFlight.id}`, { headers });
            setStandbyData(sbRes.data);
        } catch { /* silent */ }
    };

    // Auditoria 7 - Item 2: auto-refresh de reservas pendientes + pasajeros del vuelo seleccionado (30s)
    const refreshSelectedFlightData = useCallback(async () => {
        if (!selectedFlight) return;
        try {
            const [rvRes, pRes, sRes] = await Promise.all([
                axios.get(`${API}/reservations?flight_id=${selectedFlight.id}`, { headers }),
                axios.get(`${API}/counter/flights/${selectedFlight.id}/passengers`, { headers }),
                axios.get(`${API}/counter/flights/${selectedFlight.id}/summary`, { headers }),
            ]);
            setReservations(rvRes.data || []);
            setPassengers(pRes.data || []);
            setSummary(sRes.data);
        } catch { /* silent */ }
    }, [selectedFlight]);
    useAutoRefresh(refreshSelectedFlightData, 30000);

    const createStandby = async () => {
        if (!selectedFlight) return;
        if (!newStandby.passenger_name) { toast.error('Nombre del pasajero requerido'); return; }
        setSavingStandby(true);
        try {
            await axios.post(`${API}/reservations/standby`, {
                flight_id: selectedFlight.id,
                ...newStandby,
                pax_count: parseInt(newStandby.pax_count, 10) || 1,
            }, { headers });
            toast.success('Pasajero en espacio disponible');
            setShowStandbyDialog(false);
            setNewStandby({ passenger_name: '', contact_phone: '', pax_count: 1, priority: 'normal', notes: '' });
            refreshStandby();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al crear standby');
        } finally { setSavingStandby(false); }
    };

    const promoteStandby = async (id) => {
        try {
            await axios.post(`${API}/reservations/${id}/promote`, {}, { headers });
            toast.success('Standby confirmado');
            refreshStandby();
            // Refrescar reservas para que aparezca como confirmed en el panel pendientes
            try {
                const rvRes = await axios.get(`${API}/reservations?flight_id=${selectedFlight.id}`, { headers });
                setReservations(rvRes.data || []);
            } catch { /* silent */ }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No fue posible confirmar');
        }
    };

    const autoConfirmStandby = async () => {
        if (!selectedFlight) return;
        try {
            const res = await axios.post(`${API}/reservations/standby/${selectedFlight.id}/auto-confirm`, {}, { headers });
            const n = (res.data?.promoted || []).length;
            if (n === 0) toast.info('No hay asientos libres para autoconfirmar');
            else toast.success(`${n} standby confirmado(s)`);
            refreshStandby();
            try {
                const rvRes = await axios.get(`${API}/reservations?flight_id=${selectedFlight.id}`, { headers });
                setReservations(rvRes.data || []);
            } catch { /* silent */ }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al autoconfirmar');
        }
    };

    // Emite (o reutiliza si ya existe) un boarding pass y descarga el PDF.
    // El QR original se preserva en reimpresiones.
    // Check-in desde una reservación de Ventas (Auditoria 4 - Item 3 + Auditoria 7 - Item 1)
    const checkinReservation = async (reservationId) => {
        try {
            const res = await axios.post(`${API}/reservations/${reservationId}/checkin`, {}, { headers });
            const n = res.data?.passenger_ids?.length || 0;
            toast.success(`${n} pasajero(s) enviado(s) a Mostrador. Asigna asiento en el Mapa para completar el check-in.`);
            // Refresh pasajeros + reservas
            const [passRes, rvRes] = await Promise.all([
                axios.get(`${API}/counter/flights/${selectedFlight.id}/passengers`, { headers }),
                axios.get(`${API}/reservations?flight_id=${selectedFlight.id}`, { headers }),
            ]);
            setPassengers(passRes.data || []);
            setReservations(rvRes.data || []);
            // Cambiar a la pestaña de Mapa de Asientos para continuar el flujo
            setCounterTab('seatmap');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al enviar pasajeros a Mostrador');
        }
    };

    const issueOrReprintBoardingPass = async (passenger, existingBp = null) => {
        setIssuingBp(passenger.id);
        try {
            if (existingBp) {
                // Reimpresion: pedir al backend que incremente el contador y devuelva el payload original
                const res = await axios.post(`${API}/boarding-passes/${existingBp.id}/reprint`, {}, { headers });
                const bp = res.data;
                await generateBoardingPass({
                    passenger,
                    flight: selectedFlight,
                    seat: bp.seat || passenger.seat,
                    pnr: bp.pnr,
                    airline: airline?.name,
                    qrPayload: bp.qr_payload,
                    reprintCount: bp.reprint_count,
                    gate: bp.gate || selectedFlight.gate,
                    boardingTime: bp.boarding_time,
                    boardingGroup: bp.boarding_group,
                    sequenceNumber: bp.sequence_number,
                    cabinClass: bp.cabin_class,
                    originIata: bp.origin_iata || selectedFlight.origin,
                    destinationIata: bp.destination_iata || selectedFlight.destination,
                });
                toast.success(`Boarding pass reimpreso (#${bp.reprint_count})`);
                // Refrescar historial
                const bpRes = await axios.get(`${API}/boarding-passes?flight_id=${selectedFlight.id}`, { headers });
                setBoardingPasses(bpRes.data || []);
                return;
            }
            // Primera emision: computar campos operacionales + resolver IATA
            const dep = selectedFlight.scheduled_departure || '';
            const boardingTime = dep ? new Date(new Date(dep).getTime() - 35 * 60000).toISOString() : '';
            const sequenceNumber = boardingPasses.length + 1;
            // Buscar reservación del pasajero para cabin_class
            const paxRes = reservations.find(r => (r.passenger_name || '').toLowerCase().includes((passenger.first_name || '').toLowerCase()));
            const cabinClass = paxRes?.cabin_class || 'economy';
            // Resolver IATA codes
            let originIata = selectedFlight.origin;
            let destinationIata = selectedFlight.destination;
            try {
                const [o, d] = await Promise.all([
                    axios.get(`${API}/airports?icao=${selectedFlight.origin}`, { headers }),
                    axios.get(`${API}/airports?icao=${selectedFlight.destination}`, { headers }),
                ]);
                originIata = o.data?.iata || selectedFlight.origin;
                destinationIata = d.data?.iata || selectedFlight.destination;
            } catch { /* fallback ICAO */ }
            const { qrPayload } = await generateBoardingPass({
                passenger,
                flight: selectedFlight,
                seat: passenger.seat,
                pnr: passenger.pnr || passenger.ticket_number,
                airline: airline?.name,
                gate: selectedFlight.gate,
                boardingTime,
                sequenceNumber,
                cabinClass,
                originIata,
                destinationIata,
            });
            await axios.post(`${API}/boarding-passes`, {
                flight_id: selectedFlight.id,
                passenger_id: passenger.id,
                pnr: passenger.pnr || passenger.ticket_number || '',
                passenger_name: `${passenger.first_name || ''} ${passenger.last_name || ''}`.trim(),
                seat: passenger.seat || '',
                flight_number: selectedFlight.flight_number || '',
                flight_date: (selectedFlight.scheduled_departure || '').split('T')[0],
                qr_payload: qrPayload,
                gate: selectedFlight.gate || '',
                boarding_time: boardingTime,
                sequence_number: sequenceNumber,
                cabin_class: cabinClass,
                origin_iata: originIata,
                destination_iata: destinationIata,
            }, { headers });
            toast.success('Boarding pass generado');
            const bpRes = await axios.get(`${API}/boarding-passes?flight_id=${selectedFlight.id}`, { headers });
            setBoardingPasses(bpRes.data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error generando boarding pass');
        } finally {
            setIssuingBp(null);
        }
    };

    const addPassenger = async () => {
        if (!newPax.first_name || !newPax.last_name || !newPax.document_number) {
            toast.error('Nombre, apellido y documento son requeridos');
            return;
        }
        try {
            await axios.post(`${API}/counter/flights/${selectedFlight.id}/passengers`, newPax, { headers });
            toast.success('Pasajero agregado');
            setShowAddPax(false);
            setNewPax({ first_name: '', last_name: '', document_type: 'passport', document_number: '', seat: '', pax_type: 'adult', special_requirements: '', baggage_count: 1, baggage_weight_kg: 15, fare_amount: '', payment_method: 'cash', contact_email: '', contact_phone: '', cabin_class: 'economy' });
            selectFlight(selectedFlight);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al agregar pasajero'); }
    };

    const checkinPassenger = async (passengerId) => {
        const p = passengers.find(x => x.id === passengerId);
        if (p && !p.seat) {
            toast.info('Elige un asiento en el mapa para completar el check-in', { duration: 6000 });
            setCounterTab('seatmap');
            return;
        }
        try {
            await axios.patch(`${API}/counter/flights/${selectedFlight.id}/passengers/${passengerId}/checkin`, null, { headers });
            toast.success('Check-in completado');
            selectFlight(selectedFlight);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error en check-in'); }
    };

    const boardPassenger = async (passengerId) => {
        try {
            await axios.patch(`${API}/counter/flights/${selectedFlight.id}/passengers/${passengerId}/board`, null, { headers });
            toast.success('Pasajero abordado');
            selectFlight(selectedFlight);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al registrar abordaje'); }
    };

    const removePassenger = async (passengerId) => {
        try {
            await axios.delete(`${API}/counter/flights/${selectedFlight.id}/passengers/${passengerId}`, { headers });
            toast.success('Pasajero removido');
            selectFlight(selectedFlight);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al remover pasajero'); }
    };

    const openEditPax = (p) => {
        setEditPaxData({ first_name: p.first_name, last_name: p.last_name, document_type: p.document_type, document_number: p.document_number, seat: p.seat, pax_type: p.pax_type, special_requirements: p.special_requirements || '', baggage_count: p.baggage_count || 0, baggage_weight_kg: p.baggage_weight_kg || 0 });
        setEditPax(p);
    };
    const saveEditPax = async () => {
        if (!editPax) return;
        try {
            await axios.put(`${API}/counter/flights/${selectedFlight.id}/passengers/${editPax.id}`, editPaxData, { headers });
            toast.success('Pasajero actualizado');
            setEditPax(null);
            selectFlight(selectedFlight);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al actualizar pasajero'); }
    };

    const filteredPax = passengers.filter(p => {
        if (!searchPax) return true;
        const s = searchPax.toLowerCase();
        return p.first_name?.toLowerCase().includes(s) || p.last_name?.toLowerCase().includes(s) || p.document_number?.toLowerCase().includes(s) || p.seat?.toLowerCase().includes(s);
    });

    const canManage = hasRole('admin') || hasRole('counter');

    return (
        <div className="space-y-6" data-testid="counter-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="counter-title">
                        Mostrador
                    </h1>
                    <p className="text-muted-foreground mt-1">Check-in de pasajeros y manifiesto de vuelo</p>
                </div>
                <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-[160px] input-aviation font-mono" data-testid="counter-date-filter" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Flight List */}
                <div className="lg:col-span-4 space-y-3">
                    <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Vuelos del Dia</h2>
                    {loading ? (
                        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                    ) : flights.length === 0 ? (
                        <Card className="card-aviation"><CardContent className="p-8 text-center text-muted-foreground"><Plane className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No hay vuelos para esta fecha</p></CardContent></Card>
                    ) : (
                        flights.map(f => (
                            <Card
                                key={f.id}
                                className={`card-aviation cursor-pointer transition-all ${selectedFlight?.id === f.id ? 'ring-2 ring-primary' : 'hover:border-primary/30'}`}
                                onClick={() => selectFlight(f)}
                                data-testid={`counter-flight-${f.id}`}
                            >
                                <CardContent className="p-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div>
                                                <p className="font-bold font-mono text-base">{f.flight_number}</p>
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                    <span className="font-mono">{f.origin}</span>
                                                    <ArrowRight className="w-3 h-3" />
                                                    <span className="font-mono">{f.destination}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right space-y-1">
                                            <Badge className={statusColors[f.status] || 'bg-muted'}>{statusLabels[f.status] || f.status}</Badge>
                                            <div className="flex items-center gap-1 justify-end">
                                                <Users className="w-3 h-3 text-muted-foreground" />
                                                <span className={`text-xs font-mono font-bold ${f.pax_checked_in === f.pax_total && f.pax_total > 0 ? 'text-emerald-500' : ''}`}>
                                                    {f.pax_checked_in || 0}/{f.pax_total || 0}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                                        <Clock className="w-3 h-3" />
                                        <span className="font-mono">{f.scheduled_departure?.split('T')[1]?.slice(0,5) || '--:--'}</span>
                                        {f.aircraft_registration && (
                                            <Badge variant="outline" className="ml-auto text-[10px] font-mono">{f.aircraft_registration}</Badge>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>

                {/* Passenger Panel */}
                <div className="lg:col-span-8">
                    {!selectedFlight ? (
                        <Card className="card-aviation">
                            <CardContent className="p-12 text-center text-muted-foreground">
                                <Clipboard className="w-14 h-14 mx-auto mb-4 opacity-20" />
                                <p className="text-lg">Selecciona un vuelo para gestionar pasajeros</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-4">
                            {/* Flight header */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Plane className="w-5 h-5 text-primary" />
                                    <div>
                                        <h2 className="text-xl font-bold font-mono">{selectedFlight.flight_number}</h2>
                                        <p className="text-sm text-muted-foreground">{selectedFlight.origin} → {selectedFlight.destination} | {selectedFlight.scheduled_departure?.split('T')[0]}</p>
                                        {/* Auditoria 4 - Stage 3: campo Gate editable por Counter/Dispatcher/Admin */}
                                        {(hasRole(['admin', 'counter', 'dispatcher'])) && (
                                            <div className="flex items-center gap-2 mt-1" data-testid="gate-editor">
                                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Gate:</span>
                                                <Input
                                                    className="h-6 w-20 text-xs font-mono uppercase"
                                                    placeholder="—"
                                                    defaultValue={selectedFlight.gate || ''}
                                                    onBlur={async (e) => {
                                                        const v = e.target.value.trim().toUpperCase();
                                                        if (v === (selectedFlight.gate || '')) return;
                                                        try {
                                                            await axios.put(`${API}/flights/${selectedFlight.id}`, { gate: v }, { headers });
                                                            setSelectedFlight({ ...selectedFlight, gate: v });
                                                            toast.success(`Gate ${v || 'limpiado'}`);
                                                        } catch (err) {
                                                            toast.error('Error al asignar gate');
                                                        }
                                                    }}
                                                    data-testid="flight-gate-input"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {canManage && (
                                    <Button onClick={() => setShowAddPax(true)} className="btn-primary" data-testid="add-passenger-btn">
                                        <UserPlus className="w-4 h-4 mr-2" />Agregar PAX
                                    </Button>
                                )}
                            </div>

                            {/* Summary cards */}
                            {summary && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <Card className="card-aviation">
                                        <CardContent className="p-3 flex items-center gap-2">
                                            <Users className="w-5 h-5 text-primary" />
                                            <div><p className="text-xl font-bold font-mono">{summary.pax_summary.total}</p><p className="text-[10px] text-muted-foreground uppercase">Total PAX</p></div>
                                        </CardContent>
                                    </Card>
                                    <Card className="card-aviation">
                                        <CardContent className="p-3 flex items-center gap-2">
                                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                                            <div><p className="text-xl font-bold font-mono">{summary.pax_summary.checked_in}</p><p className="text-[10px] text-muted-foreground uppercase">Check-in</p></div>
                                        </CardContent>
                                    </Card>
                                    <Card className="card-aviation">
                                        <CardContent className="p-3 flex items-center gap-2">
                                            <UserCheck className="w-5 h-5 text-amber-500" />
                                            <div><p className="text-xl font-bold font-mono">{summary.pax_summary.boarded}</p><p className="text-[10px] text-muted-foreground uppercase">Abordados</p></div>
                                        </CardContent>
                                    </Card>
                                    <Card className="card-aviation">
                                        <CardContent className="p-3 flex items-center gap-2">
                                            <Package className="w-5 h-5 text-blue-400" />
                                            <div><p className="text-xl font-bold font-mono">{summary.baggage_summary.total_bags}</p><p className="text-[10px] text-muted-foreground uppercase">{summary.baggage_summary.total_weight_kg}kg</p></div>
                                        </CardContent>
                                    </Card>
                                </div>
                            )}

                            {/* Tabs: Passengers / Seat Map */}
                            <Tabs value={counterTab} onValueChange={setCounterTab}>
                                <TabsList>
                                    <TabsTrigger value="passengers" data-testid="tab-passengers"><Users className="w-3.5 h-3.5 mr-1" />Pasajeros</TabsTrigger>
                                    <TabsTrigger value="seatmap" data-testid="tab-seatmap">
                                        <LayoutGrid className="w-3.5 h-3.5 mr-1" />Mapa de Asientos
                                        {seatData?.has_seat_map && <Badge variant="outline" className="ml-1.5 text-[10px] h-4 px-1">{seatData.stats.occupied}/{seatData.stats.total}</Badge>}
                                    </TabsTrigger>
                                    <TabsTrigger value="boardingpasses" data-testid="tab-boardingpasses">
                                        <Printer className="w-3.5 h-3.5 mr-1" />Boarding Passes
                                        {boardingPasses.length > 0 && <Badge variant="outline" className="ml-1.5 text-[10px] h-4 px-1">{boardingPasses.length}</Badge>}
                                    </TabsTrigger>
                                    <TabsTrigger value="standby" data-testid="tab-standby">
                                        <TicketCheck className="w-3.5 h-3.5 mr-1" />Espacio Disp.
                                        {(standbyData?.standby?.length || 0) > 0 && <Badge variant="outline" className="ml-1.5 text-[10px] h-4 px-1 bg-amber-500/10 border-amber-500/30">{standbyData.standby.length}</Badge>}
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent value="passengers" className="mt-4 space-y-3">
                                    {/* Auditoria 4 - Item 3: Reservas pendientes (de Ventas) con check-in inline */}
                                    {(() => {
                                        const pending = reservations.filter(r => r.status === 'confirmed' && !passengers.some(p => p.reservation_id === r.id));
                                        if (pending.length === 0) return null;
                                        return (
                                            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="pending-reservations-panel">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <UserPlus className="w-4 h-4 text-amber-500" />
                                                    <h3 className="text-sm font-bold uppercase tracking-wider">Reservas pendientes de check-in ({pending.length})</h3>
                                                </div>
                                                <div className="space-y-1.5">
                                                    {pending.map(r => (
                                                        <div key={r.id} className="flex items-center justify-between p-2 rounded bg-background/60" data-testid={`pending-res-${r.id}`}>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-sm font-semibold">{r.passenger_name}</span>
                                                                    <Badge variant="outline" className="text-[10px]">{r.cabin_class || 'economy'}</Badge>
                                                                    {r.pax_count > 1 && <Badge className="text-[10px] bg-primary/10 text-primary">{r.pax_count} pax</Badge>}
                                                                    <span className="text-[10px] font-mono text-muted-foreground">{r.pnr}</span>
                                                                </div>
                                                                {r.special_requests && <p className="text-[10px] text-amber-500 mt-0.5">✱ {r.special_requests}</p>}
                                                            </div>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => checkinReservation(r.id)}
                                                                data-testid={`res-checkin-${r.id}`}
                                                                className="h-8 text-xs border-emerald-500/40 text-emerald-500"
                                                            >
                                                                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                                                Check-in
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}
                            {/* Search */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input placeholder="Buscar pasajero..." value={searchPax} onChange={e => setSearchPax(e.target.value)} className="pl-10 input-aviation" data-testid="search-passengers" />
                            </div>

                            {/* Passenger list */}
                            {paxLoading ? (
                                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                            ) : (
                                <div className="space-y-2" data-testid="passenger-list">
                                    {filteredPax.length === 0 ? (
                                        <Card className="card-aviation"><CardContent className="p-8 text-center text-muted-foreground"><Users className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No hay pasajeros registrados</p></CardContent></Card>
                                    ) : filteredPax.map(p => (
                                        <Card key={p.id} className="card-aviation" data-testid={`pax-${p.id}`}>
                                            <CardContent className="p-3">
                                                <div className="flex items-center justify-between flex-wrap gap-2">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${p.boarded ? 'bg-emerald-500/20 text-emerald-500' : p.checked_in ? 'bg-blue-500/20 text-blue-500' : 'bg-muted text-muted-foreground'}`}>
                                                            {p.first_name?.[0]}{p.last_name?.[0]}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-sm">{p.last_name}, {p.first_name}</p>
                                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                <span>{p.document_type === 'passport' ? 'Pasaporte' : p.document_type === 'id_card' ? 'Cedula' : p.document_type}: {p.document_number}</span>
                                                                {p.seat && <span className="font-mono font-bold">Asiento: {p.seat}</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant="outline" className="text-[10px]">
                                                            {p.pax_type === 'adult' ? 'ADL' : p.pax_type === 'child' ? 'CHD' : 'INF'}
                                                        </Badge>
                                                        <Badge variant="outline" className="text-[10px]">
                                                            {p.baggage_count} bag | {p.baggage_weight_kg}kg
                                                        </Badge>
                                                        {p.boarded ? (
                                                            <Badge className="bg-emerald-500/10 text-emerald-500">Abordado</Badge>
                                                        ) : p.checked_in ? (
                                                            <Badge className="bg-blue-500/10 text-blue-500">Check-in</Badge>
                                                        ) : (
                                                            <Badge className="bg-muted text-muted-foreground">Pendiente</Badge>
                                                        )}
                                                        {canManage && (
                                                            <div className="flex items-center gap-1">
                                                                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openEditPax(p)} data-testid={`edit-pax-${p.id}`}>
                                                                    <Pencil className="w-3.5 h-3.5" />
                                                                </Button>
                                                                {!p.checked_in && (
                                                                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => checkinPassenger(p.id)} data-testid={`checkin-${p.id}`}>
                                                                        <CheckCircle className="w-3.5 h-3.5 mr-1" />Check-in
                                                                    </Button>
                                                                )}
                                                                {p.checked_in && !p.seat && (
                                                                    <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-500" onClick={() => { toast.info(`Elige un asiento para ${p.first_name} ${p.last_name}`); setCounterTab('seatmap'); }} data-testid={`assign-seat-${p.id}`}>
                                                                        Asignar asiento
                                                                    </Button>
                                                                )}
                                                                {p.checked_in && !p.boarded && p.seat && (
                                                                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => boardPassenger(p.id)} data-testid={`board-${p.id}`}>
                                                                        <ChevronRight className="w-3.5 h-3.5 mr-1" />Abordar
                                                                    </Button>
                                                                )}
                                                                {/* Auditoria 3 - Item 10 + follow-up: Boarding Pass con historial y reimpresion (QR preservado) */}
                                                                {p.seat && (() => {
                                                                    const existing = boardingPasses.find(b => b.passenger_id === p.id);
                                                                    return (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-7 text-xs text-amber-500"
                                                                            onClick={() => issueOrReprintBoardingPass(p, existing)}
                                                                            disabled={issuingBp === p.id}
                                                                            data-testid={`boarding-pass-${p.id}`}
                                                                            title={existing ? `Reimprimir boarding pass (emitido ${new Date(existing.issued_at).toLocaleString('es-ES')})` : 'Emitir boarding pass'}
                                                                        >
                                                                            <Printer className="w-3.5 h-3.5" />
                                                                            {existing && (
                                                                                <span className="ml-1 text-[9px] font-bold uppercase" data-testid={`bp-reprint-badge-${p.id}`}>
                                                                                    {existing.reprint_count > 0 ? `x${existing.reprint_count + 1}` : 'RE'}
                                                                                </span>
                                                                            )}
                                                                        </Button>
                                                                    );
                                                                })()}
                                                                {!p.boarded && (
                                                                    <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" onClick={() => removePassenger(p.id)} data-testid={`remove-pax-${p.id}`}>
                                                                        <XCircle className="w-3.5 h-3.5" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                                </TabsContent>

                                <TabsContent value="seatmap" className="mt-4">
                                    <Card className="card-aviation">
                                        <CardContent className="p-4">
                                            <SeatMap
                                                seatData={seatData}
                                                flightId={selectedFlight?.id}
                                                passengers={passengers}
                                                onUpdate={() => {
                                                    if (selectedFlight) {
                                                        Promise.all([
                                                            axios.get(`${API}/flights/${selectedFlight.id}/seats`, { headers }).then(r => setSeatData(r.data)).catch(() => {}),
                                                            axios.get(`${API}/counter/flights/${selectedFlight.id}/passengers`, { headers }).then(r => setPassengers(r.data)).catch(() => {}),
                                                            axios.get(`${API}/counter/flights/${selectedFlight.id}/summary`, { headers }).then(r => setSummary(r.data)).catch(() => {}),
                                                        ]);
                                                    }
                                                }}
                                            />
                                        </CardContent>
                                    </Card>
                                </TabsContent>

                                {/* Historial de Boarding Passes emitidos */}
                                <TabsContent value="boardingpasses" className="mt-4" data-testid="tab-boardingpasses-content">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                                                <Printer className="w-4 h-4 text-primary" />
                                                Historial de Boarding Passes ({boardingPasses.length})
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            {boardingPasses.length === 0 ? (
                                                <p className="text-sm text-muted-foreground py-8 text-center">Aún no se ha emitido ningún boarding pass para este vuelo.</p>
                                            ) : (
                                                <div className="space-y-2">
                                                    {boardingPasses.map(bp => {
                                                        const pax = passengers.find(p => p.id === bp.passenger_id);
                                                        return (
                                                            <div key={bp.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50" data-testid={`bp-history-${bp.id}`}>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-semibold text-sm">{bp.passenger_name || 'Pasajero'}</span>
                                                                        <Badge variant="outline" className="text-[10px]">Asiento {bp.seat}</Badge>
                                                                        <span className="text-xs text-muted-foreground font-mono">{bp.pnr}</span>
                                                                        {bp.reprint_count > 0 && (
                                                                            <Badge className="text-[10px] bg-amber-500/10 text-amber-500 border-amber-500/40">Reimpreso ×{bp.reprint_count}</Badge>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-[10px] text-muted-foreground mt-0.5">
                                                                        Emitido: {new Date(bp.issued_at).toLocaleString('es-ES')} por {bp.issued_by_name || '—'}
                                                                        {bp.last_reprint_at && <> · Última reimpresión: {new Date(bp.last_reprint_at).toLocaleString('es-ES')}</>}
                                                                    </p>
                                                                </div>
                                                                {pax && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => issueOrReprintBoardingPass(pax, bp)}
                                                                        disabled={issuingBp === pax.id}
                                                                        data-testid={`bp-reprint-btn-${bp.id}`}
                                                                        className="h-8 text-xs"
                                                                    >
                                                                        <Printer className="w-3.5 h-3.5 mr-1" />
                                                                        Reimprimir
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                </TabsContent>

                                {/* Stage 4 - Item 18: Espacio disponible / Standby */}
                                <TabsContent value="standby" className="mt-4 space-y-3" data-testid="tab-standby-content">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="text-xs text-muted-foreground">
                                            {standbyData ? (
                                                <>
                                                    Capacidad: <span className="text-foreground font-medium">{standbyData.capacity || '—'}</span>
                                                    {' '}· Ocupados: <span className="text-foreground font-medium">{standbyData.used}</span>
                                                    {' '}· Libres: <span className={`font-bold ${standbyData.free_seats > 0 ? 'text-emerald-500' : 'text-red-500'}`}>{standbyData.free_seats}</span>
                                                </>
                                            ) : 'Cargando...'}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={autoConfirmStandby} disabled={!standbyData || standbyData.standby.length === 0 || standbyData.free_seats === 0} data-testid="standby-autoconfirm">
                                                <Zap className="w-3.5 h-3.5 mr-1" />Autoconfirmar
                                            </Button>
                                            <Button size="sm" className="btn-primary h-8 text-xs" onClick={() => setShowStandbyDialog(true)} data-testid="standby-add">
                                                <Plus className="w-3.5 h-3.5 mr-1" />Nuevo standby
                                            </Button>
                                        </div>
                                    </div>
                                    {(!standbyData || standbyData.standby.length === 0) ? (
                                        <div className="rounded-lg border border-border/30 bg-muted/10 p-8 text-center text-sm text-muted-foreground">
                                            Sin pasajeros en espacio disponible para este vuelo.
                                        </div>
                                    ) : (
                                        <div className="space-y-1.5">
                                            {standbyData.standby.map(s => (
                                                <div key={s.id} className="rounded-lg border border-border/40 bg-background/50 p-2.5 flex items-center justify-between gap-2 flex-wrap" data-testid={`standby-row-${s.id}`}>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-sm truncate">{s.passenger_name}</span>
                                                            <Badge variant="outline" className={`text-[10px] ${s.priority === 'staff' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' : s.priority === 'employee' ? 'bg-purple-500/10 text-purple-400 border-purple-500/30' : 'bg-muted/40 text-muted-foreground border-border/40'}`}>
                                                                {s.priority || 'normal'}
                                                            </Badge>
                                                            <span className="text-[11px] text-muted-foreground">·{' '}{s.pax_count} pax</span>
                                                            {s.pnr && <span className="text-[10px] text-muted-foreground">· {s.pnr}</span>}
                                                        </div>
                                                        <div className="text-[10px] text-muted-foreground">
                                                            Desde: {s.standby_since ? new Date(s.standby_since).toLocaleString('es', { hour12: false }) : '—'}
                                                            {s.notes ? ` · ${s.notes}` : ''}
                                                        </div>
                                                    </div>
                                                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => promoteStandby(s.id)} disabled={standbyData.free_seats < (s.pax_count || 1)} data-testid={`standby-promote-${s.id}`}>
                                                        <CheckCircle className="w-3.5 h-3.5 mr-1" />Confirmar
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </TabsContent>
                            </Tabs>
                        </div>
                    )}
                </div>
            </div>

            {/* Standby Dialog */}
            <Dialog open={showStandbyDialog} onOpenChange={setShowStandbyDialog}>
                <DialogContent className="sm:max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase tracking-tight flex items-center gap-2">
                            <TicketCheck className="w-5 h-5 text-primary" />
                            Nuevo Standby — {selectedFlight?.flight_number}
                        </DialogTitle>
                        <DialogDescription>
                            Registra un pasajero en espacio disponible. No descuenta capacidad hasta ser confirmado.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label>Pasajero</Label>
                            <Input value={newStandby.passenger_name} onChange={e => setNewStandby({ ...newStandby, passenger_name: e.target.value })} placeholder="Nombre completo" data-testid="standby-passenger-name" />
                        </div>
                        <div className="grid grid-cols-[1fr_100px] gap-3">
                            <div className="space-y-1.5">
                                <Label>Teléfono</Label>
                                <Input value={newStandby.contact_phone} onChange={e => setNewStandby({ ...newStandby, contact_phone: e.target.value })} placeholder="Opcional" data-testid="standby-phone" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Pax</Label>
                                <Input type="number" min="1" value={newStandby.pax_count} onChange={e => setNewStandby({ ...newStandby, pax_count: e.target.value })} data-testid="standby-pax-count" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Prioridad</Label>
                            <Select value={newStandby.priority} onValueChange={v => setNewStandby({ ...newStandby, priority: v })}>
                                <SelectTrigger data-testid="standby-priority"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="normal">Normal (pasajero comercial)</SelectItem>
                                    <SelectItem value="employee">Empleado</SelectItem>
                                    <SelectItem value="staff">Staff/Comercial (prioridad alta)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Notas</Label>
                            <Input value={newStandby.notes} onChange={e => setNewStandby({ ...newStandby, notes: e.target.value })} placeholder="Motivo o referencia" data-testid="standby-notes" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowStandbyDialog(false)}>Cancelar</Button>
                        <Button onClick={createStandby} disabled={savingStandby || !newStandby.passenger_name} className="btn-primary" data-testid="standby-save">
                            {savingStandby && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Registrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Passenger Dialog */}
            <Dialog open={showAddPax} onOpenChange={setShowAddPax}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase tracking-tight flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-primary" />
                            Agregar Pasajero — {selectedFlight?.flight_number}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Nombre</Label>
                                <Input value={newPax.first_name} onChange={e => setNewPax({...newPax, first_name: e.target.value})} placeholder="Nombre" data-testid="pax-first-name" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Apellido</Label>
                                <Input value={newPax.last_name} onChange={e => setNewPax({...newPax, last_name: e.target.value})} placeholder="Apellido" data-testid="pax-last-name" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Tipo Doc.</Label>
                                <Select value={newPax.document_type} onValueChange={v => setNewPax({...newPax, document_type: v})}>
                                    <SelectTrigger data-testid="pax-doc-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="passport">Pasaporte</SelectItem>
                                        <SelectItem value="id_card">Cedula</SelectItem>
                                        <SelectItem value="other">Otro</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>No. Documento</Label>
                                <Input value={newPax.document_number} onChange={e => setNewPax({...newPax, document_number: e.target.value})} placeholder="A12345678" data-testid="pax-doc-number" />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1.5">
                                <Label>Asiento</Label>
                                <Input value={newPax.seat} onChange={e => setNewPax({...newPax, seat: e.target.value.toUpperCase()})} placeholder="12A" data-testid="pax-seat" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Tipo PAX</Label>
                                <Select value={newPax.pax_type} onValueChange={v => setNewPax({...newPax, pax_type: v})}>
                                    <SelectTrigger data-testid="pax-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="adult">Adulto</SelectItem>
                                        <SelectItem value="child">Menor</SelectItem>
                                        <SelectItem value="infant">Infante</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Equipaje (pzas)</Label>
                                <Input type="number" min="0" max="10" value={newPax.baggage_count} onChange={e => setNewPax({...newPax, baggage_count: parseInt(e.target.value) || 0})} data-testid="pax-bags" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Peso equipaje (kg)</Label>
                                <Input type="number" min="0" value={newPax.baggage_weight_kg} onChange={e => setNewPax({...newPax, baggage_weight_kg: parseFloat(e.target.value) || 0})} data-testid="pax-bag-weight" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Req. Especiales</Label>
                                <Input value={newPax.special_requirements} onChange={e => setNewPax({...newPax, special_requirements: e.target.value})} placeholder="WCHR, UMNR..." data-testid="pax-special" />
                            </div>
                        </div>
                        {/* Auditoria 6 - Item 10: campos comerciales igualados con Ventas */}
                        <div className="pt-2 border-t border-border/40">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Comercial (walk-up)</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>Tarifa</Label>
                                    <Input type="number" step="0.01" min="0" value={newPax.fare_amount} onChange={e => setNewPax({...newPax, fare_amount: e.target.value})} placeholder="450.00" data-testid="pax-fare" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Método de pago</Label>
                                    <Select value={newPax.payment_method} onValueChange={v => setNewPax({...newPax, payment_method: v})}>
                                        <SelectTrigger data-testid="pax-payment"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="cash">Efectivo</SelectItem>
                                            <SelectItem value="card">Tarjeta</SelectItem>
                                            <SelectItem value="transfer">Transferencia</SelectItem>
                                            <SelectItem value="voucher">Voucher</SelectItem>
                                            <SelectItem value="corporate">Corporativo</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-3">
                                <div className="space-y-1.5">
                                    <Label>Email contacto</Label>
                                    <Input type="email" value={newPax.contact_email} onChange={e => setNewPax({...newPax, contact_email: e.target.value})} placeholder="pasajero@ejemplo.com" data-testid="pax-email" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Teléfono contacto</Label>
                                    <Input value={newPax.contact_phone} onChange={e => setNewPax({...newPax, contact_phone: e.target.value})} placeholder="+502 5555 5555" data-testid="pax-phone" />
                                </div>
                            </div>
                            <div className="space-y-1.5 mt-3">
                                <Label>Clase</Label>
                                <Select value={newPax.cabin_class} onValueChange={v => setNewPax({...newPax, cabin_class: v})}>
                                    <SelectTrigger data-testid="pax-class"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="economy">Económica</SelectItem>
                                        <SelectItem value="economy_flex">Económica Flex</SelectItem>
                                        <SelectItem value="business">Ejecutiva</SelectItem>
                                        <SelectItem value="first">Primera</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddPax(false)}>Cancelar</Button>
                        <Button onClick={addPassenger} className="btn-primary" data-testid="submit-add-pax">
                            <UserPlus className="w-4 h-4 mr-2" />Agregar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Passenger Dialog */}
            <Dialog open={!!editPax} onOpenChange={() => setEditPax(null)}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase tracking-tight flex items-center gap-2">
                            <Pencil className="w-5 h-5 text-primary" />Editar Pasajero
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Nombre</Label><Input value={editPaxData.first_name || ''} onChange={e => setEditPaxData({...editPaxData, first_name: e.target.value})} data-testid="edit-pax-first" /></div>
                            <div className="space-y-1.5"><Label>Apellido</Label><Input value={editPaxData.last_name || ''} onChange={e => setEditPaxData({...editPaxData, last_name: e.target.value})} data-testid="edit-pax-last" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Tipo Doc.</Label>
                                <Select value={editPaxData.document_type || 'passport'} onValueChange={v => setEditPaxData({...editPaxData, document_type: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="passport">Pasaporte</SelectItem><SelectItem value="id_card">Cedula</SelectItem><SelectItem value="other">Otro</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5"><Label>No. Doc.</Label><Input value={editPaxData.document_number || ''} onChange={e => setEditPaxData({...editPaxData, document_number: e.target.value})} data-testid="edit-pax-doc" /></div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1.5"><Label>Asiento</Label><Input value={editPaxData.seat || ''} onChange={e => setEditPaxData({...editPaxData, seat: e.target.value.toUpperCase()})} data-testid="edit-pax-seat" /></div>
                            <div className="space-y-1.5"><Label>Equipaje</Label><Input type="number" min="0" value={editPaxData.baggage_count || 0} onChange={e => setEditPaxData({...editPaxData, baggage_count: parseInt(e.target.value)||0})} /></div>
                            <div className="space-y-1.5"><Label>Peso (kg)</Label><Input type="number" min="0" value={editPaxData.baggage_weight_kg || 0} onChange={e => setEditPaxData({...editPaxData, baggage_weight_kg: parseFloat(e.target.value)||0})} /></div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditPax(null)}>Cancelar</Button>
                        <Button onClick={saveEditPax} className="btn-primary" data-testid="save-edit-pax">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
